import type { Segment, TrackPoint, TrackingSport } from "@/lib/tracking/types";
import {
  validActivityChoice,
  type ActivityChoice,
  type RecordingActivity,
} from "./session-activity";
import { SENSOR_SESSION_CONSENT_VERSION } from "./session-provenance";

export const GPS_EVIDENCE_VERSION = "gps-evidence-v1" as const;
export const GPS_EVIDENCE_MAX_POINTS = 100_000;
export const GPS_EVIDENCE_MAX_BYTES = 8 * 1024 * 1024;
export const GPS_EVIDENCE_MAX_SEGMENTS = 2048;
const MAX_DURATION = 24 * 60 * 60 * 1000;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KINDS = [
  "run",
  "lift",
  "climb",
  "idle",
  "moving",
  "paddle",
  "wave",
] as const;

/** Full recorded fixes, with time stored as an offset to reduce local storage.
 * No interpolation, downsampling, synthetic accuracy or device attestation.
 */
export type EvidenceFix = [
  offsetMs: number,
  lat: number,
  lng: number,
  altitudeM: number | null,
  accuracyM: number | null,
  speedMs: number | null,
];
export type EvidenceSegment = {
  kind: Segment["kind"];
  startMs: number;
  endMs: number;
  runId: string | null;
  liftId: string | null;
  matchConfidence: number | null;
};
export type RecordingEvidence = {
  version: typeof GPS_EVIDENCE_VERSION;
  sessionId: string;
  sport: TrackingSport;
  activity: RecordingActivity;
  startedAt: number;
  endedAt: number;
  points: EvidenceFix[];
  segments: EvidenceSegment[];
};
export type EvidenceUnavailableReason =
  | "not-captured"
  | "source"
  | "activity"
  | "incomplete"
  | "identity"
  | "missing-points"
  | "invalid-points"
  | "conflicting-points"
  | "too-large"
  | "invalid-segments"
  | "not-retained";
export type RecordingEvidenceSummary =
  | {
      version: typeof GPS_EVIDENCE_VERSION;
      state: "stored";
      points: number;
      bytes: number;
    }
  | {
      version: typeof GPS_EVIDENCE_VERSION;
      state: "unavailable";
      reason: EvidenceUnavailableReason;
    };
export type EvidenceSession = {
  id: string;
  sport: TrackingSport;
  startedAt: number;
  endedAt: number;
  consentVersion?: string | null;
  stats: { activityChoice?: ActivityChoice };
  segments: Segment[];
};
export type EvidenceJournal = {
  sessionId: string;
  sport: TrackingSport;
  startedAt: number;
  capture?: unknown;
  gpsEvidenceVersion?: unknown;
  activityChoice?: ActivityChoice;
};
export type EvidenceBuild = {
  evidence: RecordingEvidence | null;
  summary: RecordingEvidenceSummary;
};
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const exact = (value: Record<string, unknown>, fields: string[]) =>
  Object.keys(value).length === fields.length &&
  fields.every((field) => Object.hasOwn(value, field));
const finite = (value: unknown, min: number, max: number): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= min &&
  value <= max;
const integer = (value: unknown, min: number, max: number): value is number =>
  finite(value, min, max) && Number.isSafeInteger(value);
const nullable = (value: unknown, min: number, max: number) =>
  value === null || finite(value, min, max);
const sourceId = (value: unknown) =>
  value === null ||
  (typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 160 &&
    !/[\u0000-\u001f\u007f]/.test(value));
const epoch = (value: unknown): value is number =>
  integer(value, 946_684_800_000, 4_102_444_800_000);
export function unavailableRecordingEvidence(
  reason: EvidenceUnavailableReason,
): EvidenceBuild {
  return {
    evidence: null,
    summary: { version: GPS_EVIDENCE_VERSION, state: "unavailable", reason },
  };
}
export function evidenceSummaryLabel(
  summary: RecordingEvidenceSummary | undefined,
): string {
  if (summary?.state === "stored")
    return "GPS timing and accuracy kept privately on this device.";
  return "This session has no complete GPS evidence for new challenge entries.";
}
export function preserveEvidenceVersion(
  saved: unknown,
  incoming: unknown,
): typeof GPS_EVIDENCE_VERSION | undefined {
  if (
    (saved !== undefined && saved !== GPS_EVIDENCE_VERSION) ||
    (incoming !== undefined && incoming !== GPS_EVIDENCE_VERSION) ||
    (incoming !== undefined && saved !== incoming)
  )
    throw new Error("Recovery cannot change when GPS evidence capture began.");
  return saved === GPS_EVIDENCE_VERSION ? saved : undefined;
}
function validFix(value: unknown, duration: number): value is EvidenceFix {
  return (
    Array.isArray(value) &&
    value.length === 6 &&
    integer(value[0], 0, duration) &&
    finite(value[1], -90, 90) &&
    finite(value[2], -180, 180) &&
    nullable(value[3], -2000, 20_000) &&
    nullable(value[4], 0, 100_000) &&
    nullable(value[5], 0, 1000)
  );
}
function validSegment(
  value: unknown,
  duration: number,
): value is EvidenceSegment {
  return (
    object(value) &&
    exact(value, [
      "kind",
      "startMs",
      "endMs",
      "runId",
      "liftId",
      "matchConfidence",
    ]) &&
    typeof value.kind === "string" &&
    (KINDS as readonly string[]).includes(value.kind) &&
    integer(value.startMs, 0, duration) &&
    integer(value.endMs, value.startMs, duration) &&
    sourceId(value.runId) &&
    sourceId(value.liftId) &&
    nullable(value.matchConfidence, 0, 1)
  );
}
/** Network callers must additionally bind identity, receipt time and admission.
 * Shape validation alone does not make a recording competition-eligible.
 */
export function validRecordingEvidence(
  value: unknown,
): value is RecordingEvidence {
  if (
    !object(value) ||
    !exact(value, [
      "version",
      "sessionId",
      "sport",
      "activity",
      "startedAt",
      "endedAt",
      "points",
      "segments",
    ]) ||
    value.version !== GPS_EVIDENCE_VERSION ||
    typeof value.sessionId !== "string" ||
    !UUID.test(value.sessionId) ||
    typeof value.sport !== "string" ||
    !["ski", "mtb", "coastal", "surf"].includes(value.sport) ||
    !validActivityChoice(
      { version: "activity-choice-v1", activity: value.activity },
      value.sport as TrackingSport,
    ) ||
    !epoch(value.startedAt) ||
    !epoch(value.endedAt) ||
    value.endedAt <= value.startedAt ||
    value.endedAt - value.startedAt > MAX_DURATION ||
    !Array.isArray(value.points) ||
    value.points.length < 2 ||
    value.points.length > GPS_EVIDENCE_MAX_POINTS ||
    !Array.isArray(value.segments) ||
    value.segments.length > GPS_EVIDENCE_MAX_SEGMENTS
  )
    return false;
  const duration = value.endedAt - value.startedAt;
  let previous = -1;
  for (const point of value.points) {
    if (!validFix(point, duration) || point[0] <= previous) return false;
    previous = point[0];
  }
  let previousEnd = -1;
  for (const segment of value.segments) {
    if (!validSegment(segment, duration) || segment.startMs < previousEnd)
      return false;
    previousEnd = segment.endMs;
  }
  try {
    return (
      new TextEncoder().encode(JSON.stringify(value)).byteLength <=
      GPS_EVIDENCE_MAX_BYTES
    );
  } catch {
    return false;
  }
}
/** Called only while finishing the owned journal. Keep invalid or incomplete
 * recordings as personal history, without inventing missing competition proof.
 */
export function buildRecordingEvidence(
  journal: EvidenceJournal | undefined,
  session: EvidenceSession,
  rawPoints: readonly TrackPoint[],
  complete: boolean,
): EvidenceBuild {
  if (journal?.gpsEvidenceVersion !== GPS_EVIDENCE_VERSION)
    return unavailableRecordingEvidence("not-captured");
  if (
    journal.capture !== "device-gps" ||
    session.consentVersion !== SENSOR_SESSION_CONSENT_VERSION
  )
    return unavailableRecordingEvidence("source");
  if (
    !validActivityChoice(journal.activityChoice, journal.sport) ||
    !validActivityChoice(session.stats.activityChoice, session.sport) ||
    journal.activityChoice.activity !== session.stats.activityChoice.activity
  )
    return unavailableRecordingEvidence("activity");
  if (!complete) return unavailableRecordingEvidence("incomplete");
  if (
    !UUID.test(session.id) ||
    journal.sessionId !== session.id ||
    journal.sport !== session.sport ||
    journal.startedAt !== session.startedAt ||
    !epoch(session.startedAt) ||
    !epoch(session.endedAt) ||
    session.endedAt <= session.startedAt ||
    session.endedAt - session.startedAt > MAX_DURATION
  )
    return unavailableRecordingEvidence("identity");
  if (
    rawPoints.length > GPS_EVIDENCE_MAX_POINTS ||
    session.segments.length > GPS_EVIDENCE_MAX_SEGMENTS
  )
    return unavailableRecordingEvidence("too-large");
  if (rawPoints.length < 2)
    return unavailableRecordingEvidence("missing-points");
  const duration = session.endedAt - session.startedAt;
  const points: EvidenceFix[] = [];
  // Native and browser buffers can overlap. Identical duplicate fixes are one
  // observation; conflicting values at the same instant are not silently chosen.
  for (const point of [...rawPoints].sort((a, b) => a.t - b.t)) {
    const encoded: EvidenceFix = [
      point.t - session.startedAt,
      point.lat,
      point.lng,
      point.ele,
      point.accuracy,
      point.speedMs ?? null,
    ];
    if (!validFix(encoded, duration))
      return unavailableRecordingEvidence("invalid-points");
    const previous = points.at(-1);
    if (previous?.[0] === encoded[0]) {
      if (previous.some((value, index) => value !== encoded[index]))
        return unavailableRecordingEvidence("conflicting-points");
    } else points.push(encoded);
  }
  if (points.length < 2) return unavailableRecordingEvidence("missing-points");
  const segments: EvidenceSegment[] = session.segments.map((segment) => ({
    kind: segment.kind,
    startMs: segment.startT - session.startedAt,
    endMs: segment.endT - session.startedAt,
    runId: segment.runId ?? null,
    liftId: segment.liftId ?? null,
    matchConfidence: segment.matchConfidence ?? null,
  }));
  let previousEnd = -1;
  for (const segment of segments) {
    if (!validSegment(segment, duration) || segment.startMs < previousEnd)
      return unavailableRecordingEvidence("invalid-segments");
    previousEnd = segment.endMs;
  }
  const evidence: RecordingEvidence = {
    version: GPS_EVIDENCE_VERSION,
    sessionId: session.id,
    sport: session.sport,
    activity: journal.activityChoice.activity,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    points,
    segments,
  };
  const bytes = new TextEncoder().encode(JSON.stringify(evidence)).byteLength;
  if (bytes > GPS_EVIDENCE_MAX_BYTES)
    return unavailableRecordingEvidence("too-large");
  return {
    evidence,
    summary: {
      version: GPS_EVIDENCE_VERSION,
      state: "stored",
      points: points.length,
      bytes,
    },
  };
}
